import React, { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { uploadImage } from '../utils/uploadImage';

const STATUS_OPTIONS = ['Entwurf', 'In Bearbeitung', 'Bereit', 'Fertig'];
const STATUS_COLORS = {
  'Entwurf':        'bg-gray-100 text-gray-500',
  'In Bearbeitung': 'bg-amber-50 text-amber-600',
  'Bereit':         'bg-indigo-50 text-indigo-600',
  'Fertig':         'bg-emerald-50 text-emerald-700',
};

// ─── Text Block ───────────────────────────────────────────────────────────────
function TextBlock({ block, onChange, onDelete, onAddBelow, isFocused, onFocus }) {
  const [editing, setEditing] = useState(false);
  const taRef = useRef(null);

  return (
    <div className="group/block relative" onMouseEnter={onFocus}>
      <div className={`absolute -left-8 top-1 flex flex-col gap-1 transition-opacity ${isFocused ? 'opacity-100' : 'opacity-0 group-hover/block:opacity-100'}`}>
        <button onClick={onDelete} className="w-6 h-6 rounded-md bg-white border border-gray-100 hover:border-red-200 hover:text-red-500 flex items-center justify-center text-gray-300 shadow-sm transition-all">
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 1L7 7M7 1L1 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
        </button>
      </div>

      {editing ? (
        <textarea
          ref={taRef}
          autoFocus
          value={block.content}
          onChange={e => onChange({ ...block, content: e.target.value })}
          onBlur={() => setEditing(false)}
          className="w-full bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm font-mono text-gray-700 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50 transition-all leading-relaxed resize-none"
          rows={Math.max(6, (block.content || '').split('\n').length + 2)}
          placeholder="Skript in Markdown…"
          spellCheck={false}
        />
      ) : (
        <div className="prose-custom cursor-text min-h-[3rem] px-1 py-0.5 rounded-xl hover:bg-gray-50/80 transition-colors" onClick={() => setEditing(true)}>
          {block.content
            ? <ReactMarkdown>{block.content}</ReactMarkdown>
            : <p className="text-gray-300 text-sm">Text-Block – klicken zum Bearbeiten…</p>
          }
        </div>
      )}

      <AddBar onAdd={onAddBelow} isFocused={isFocused} />
    </div>
  );
}

// ─── Image Block ──────────────────────────────────────────────────────────────
function ImageBlock({ block, onChange, onDelete, onAddBelow, isFocused, onFocus, shareToken }) {
  const fileRef  = useRef(null);
  const [editCap, setEditCap] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const url = await uploadImage(ev.target.result, shareToken, block.id);
          onChange({ ...block, imageUrl: url });
        } catch (err) {
          setUploadError('Upload fehlgeschlagen – bitte erneut versuchen.');
          console.error(err);
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setUploadError('Datei konnte nicht gelesen werden.');
      setUploading(false);
    }
  };

  return (
    <div className="group/block relative" onMouseEnter={onFocus}>
      <div className={`absolute -left-8 top-1 flex flex-col gap-1 transition-opacity ${isFocused ? 'opacity-100' : 'opacity-0 group-hover/block:opacity-100'}`}>
        <button onClick={onDelete} className="w-6 h-6 rounded-md bg-white border border-gray-100 hover:border-red-200 hover:text-red-500 flex items-center justify-center text-gray-300 shadow-sm transition-all">
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 1L7 7M7 1L1 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
        </button>
      </div>

      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />

      <div className="rounded-2xl overflow-hidden border border-gray-100 bg-gray-50">
        {uploading ? (
          <div className="h-48 flex flex-col items-center justify-center gap-2 text-gray-400 text-sm">
            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
            Wird hochgeladen…
          </div>
        ) : block.imageUrl ? (
          <div className="relative group/img">
            <img src={block.imageUrl} alt={block.imageCaption || ''} className="w-full max-h-80 object-cover" />
            <button onClick={() => fileRef.current?.click()} className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover/img:opacity-100">
              <span className="text-white text-xs font-medium bg-black/40 px-3 py-1.5 rounded-lg">Foto ersetzen</span>
            </button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()} className="w-full h-48 flex flex-col items-center justify-center gap-2 text-gray-300 hover:text-indigo-400 hover:bg-indigo-50/50 transition-all">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <rect x="2" y="2" width="24" height="24" rx="4" stroke="currentColor" strokeWidth="1.5"/>
              <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.3"/>
              <path d="M2 19l7-6 5 5 4-4 8 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            <span className="text-sm font-medium">Foto hinzufügen</span>
          </button>
        )}
        <div className="px-4 py-2 border-t border-gray-100">
          {uploadError && <p className="text-xs text-red-500 mb-1">{uploadError}</p>}
          {editCap ? (
            <input autoFocus value={block.imageCaption || ''} onChange={e => onChange({ ...block, imageCaption: e.target.value })} onBlur={() => setEditCap(false)} placeholder="Bildunterschrift / Regie-Notiz…" className="w-full text-xs text-gray-500 bg-transparent outline-none" />
          ) : (
            <p className="text-xs text-gray-400 cursor-text hover:text-gray-600" onClick={() => setEditCap(true)}>{block.imageCaption || 'Bildunterschrift hinzufügen…'}</p>
          )}
        </div>
      </div>

      <AddBar onAdd={onAddBelow} isFocused={isFocused} />
    </div>
  );
}

// ─── Add bar between blocks ───────────────────────────────────────────────────
function AddBar({ onAdd, isFocused }) {
  return (
    <div className={`flex items-center gap-2 mt-2 transition-opacity ${isFocused ? 'opacity-100' : 'opacity-0 group-hover/block:opacity-100'}`}>
      <div className="flex-1 h-px bg-gray-100" />
      <button onClick={() => onAdd('text')} className="text-xs text-gray-400 hover:text-indigo-600 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-indigo-50 transition-all">
        <svg width="9" height="9" viewBox="0 0 9 9" fill="none"><path d="M4.5 1V8M1 4.5H8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
        Text
      </button>
      <button onClick={() => onAdd('image')} className="text-xs text-gray-400 hover:text-indigo-600 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-indigo-50 transition-all">
        <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><rect x="1" y="1" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1.2"/><circle cx="3.5" cy="3.5" r="1" stroke="currentColor" strokeWidth="1"/><path d="M1 7l2.5-2 2 2 1.5-1.5L9 7" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/></svg>
        Foto
      </button>
      <div className="flex-1 h-px bg-gray-100" />
    </div>
  );
}

// ─── Share Modal ──────────────────────────────────────────────────────────────
function ShareModal({ shareToken, onClose }) {
  const url = `${window.location.origin}/s/${shareToken}`;
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">Kunden-Link teilen</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Schicke diesen Link deinem Kunden. Er kann das Video anschauen und direkt Feedback hinterlassen – ohne Login.
        </p>
        <div className="flex gap-2">
          <input
            readOnly
            value={url}
            className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2.5 outline-none bg-gray-50 text-gray-600 font-mono text-xs"
          />
          <button
            onClick={copy}
            className={`shrink-0 px-4 py-2 text-sm font-medium rounded-xl transition-all ${copied ? 'bg-emerald-600 text-white' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
          >
            {copied ? 'Kopiert ✓' : 'Kopieren'}
          </button>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-3 text-xs text-indigo-500 hover:text-indigo-700 text-center"
        >
          Vorschau öffnen →
        </a>
      </div>
    </div>
  );
}

// ─── Main Editor ──────────────────────────────────────────────────────────────
export default function ProjectEditor({ project, onChange }) {
  const [focusedId, setFocusedId]     = useState(null);
  const [editTitle, setEditTitle]     = useState(false);
  const [showStatus, setShowStatus]   = useState(false);
  const [showShare, setShowShare]     = useState(false);

  const updateBlock = (updated) =>
    onChange({ ...project, blocks: project.blocks.map(b => b.id === updated.id ? updated : b) });

  const deleteBlock = (id) =>
    onChange({ ...project, blocks: project.blocks.filter(b => b.id !== id) });

  const addBlockAfter = (afterId, type) => {
    const nb = { id: Date.now(), type, content: '', imageUrl: '', imageCaption: '' };
    const idx = project.blocks.findIndex(b => b.id === afterId);
    const blocks = [...project.blocks];
    blocks.splice(idx + 1, 0, nb);
    onChange({ ...project, blocks });
    setFocusedId(nb.id);
  };

  const addBlockAtEnd = (type) => {
    const nb = { id: Date.now(), type, content: '', imageUrl: '', imageCaption: '' };
    onChange({ ...project, blocks: [...project.blocks, nb] });
    setFocusedId(nb.id);
  };

  return (
    <>
      {showShare && <ShareModal shareToken={project.shareToken} onClose={() => setShowShare(false)} />}

      <div style={{ height: 'calc(100vh - 56px)' }} className="overflow-y-auto">
        <div className="max-w-3xl mx-auto px-12 py-10">

          {/* Title + meta */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              {editTitle ? (
                <input
                  autoFocus
                  value={project.title}
                  onChange={e => onChange({ ...project, title: e.target.value })}
                  onBlur={() => setEditTitle(false)}
                  onKeyDown={e => e.key === 'Enter' && setEditTitle(false)}
                  className="text-2xl font-semibold text-gray-900 bg-transparent border-b-2 border-indigo-400 outline-none flex-1"
                />
              ) : (
                <h1 className="text-2xl font-semibold text-gray-900 cursor-text hover:text-indigo-700 transition-colors flex-1" onClick={() => setEditTitle(true)}>
                  {project.title}
                </h1>
              )}
              {/* Status */}
              <div className="relative">
                <button onClick={() => setShowStatus(s => !s)} className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_COLORS[project.status]}`}>
                  {project.status}
                </button>
                {showStatus && (
                  <div className="absolute right-0 top-8 bg-white rounded-xl border border-gray-100 shadow-lg py-1 z-10 min-w-[140px]">
                    {STATUS_OPTIONS.map(s => (
                      <button key={s} onClick={() => { onChange({ ...project, status: s }); setShowStatus(false); }} className={`w-full text-left text-xs px-3 py-2 hover:bg-gray-50 font-medium ${project.status === s ? 'text-indigo-600' : 'text-gray-600'}`}>{s}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400">{project.createdAt}</p>
              {/* Share button */}
              <button
                onClick={() => setShowShare(true)}
                className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 hover:border-indigo-300 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-all"
              >
                <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                  <circle cx="11" cy="2.5" r="1.5" stroke="currentColor" strokeWidth="1.2"/>
                  <circle cx="11" cy="11.5" r="1.5" stroke="currentColor" strokeWidth="1.2"/>
                  <circle cx="2.5" cy="7" r="1.5" stroke="currentColor" strokeWidth="1.2"/>
                  <path d="M4 7l5.5-4M4 7l5.5 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
                Kunden-Link teilen
              </button>
            </div>
          </div>

          {/* Empty state */}
          {project.blocks.length === 0 && (
            <div className="flex items-center gap-3 py-8">
              <button onClick={() => addBlockAtEnd('text')} className="flex items-center gap-2 text-sm text-gray-400 hover:text-indigo-600 px-4 py-2 rounded-xl border border-dashed border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                Text hinzufügen
              </button>
              <button onClick={() => addBlockAtEnd('image')} className="flex items-center gap-2 text-sm text-gray-400 hover:text-indigo-600 px-4 py-2 rounded-xl border border-dashed border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><rect x="1" y="1" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3"/><circle cx="4" cy="4" r="1.2" stroke="currentColor" strokeWidth="1.1"/><path d="M1 8.5l3-3 2.5 2.5 2-2 3 3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/></svg>
                Foto hinzufügen
              </button>
            </div>
          )}

          {/* Blocks */}
          <div className="flex flex-col gap-4">
            {project.blocks.map(block =>
              block.type === 'text' ? (
                <TextBlock
                  key={block.id}
                  block={block}
                  onChange={updateBlock}
                  onDelete={() => deleteBlock(block.id)}
                  onAddBelow={type => addBlockAfter(block.id, type)}
                  isFocused={focusedId === block.id}
                  onFocus={() => setFocusedId(block.id)}
                />
              ) : (
                <ImageBlock
                  key={block.id}
                  block={block}
                  onChange={updateBlock}
                  onDelete={() => deleteBlock(block.id)}
                  onAddBelow={type => addBlockAfter(block.id, type)}
                  isFocused={focusedId === block.id}
                  onFocus={() => setFocusedId(block.id)}
                  shareToken={project.shareToken}
                />
              )
            )}
          </div>

          {/* Bottom add */}
          {project.blocks.length > 0 && (
            <div className="mt-6 flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-100" />
              <button onClick={() => addBlockAtEnd('text')} className="text-xs text-gray-400 hover:text-indigo-600 flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-indigo-50 transition-all">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M5 1V9M1 5H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                Textblock
              </button>
              <button onClick={() => addBlockAtEnd('image')} className="text-xs text-gray-400 hover:text-indigo-600 flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-indigo-50 transition-all">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><rect x="1" y="1" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1.2"/><circle cx="3.5" cy="3.5" r="1" stroke="currentColor" strokeWidth="1"/><path d="M1 7l2.5-2 2 2 1.5-1.5L9 7" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/></svg>
                Foto
              </button>
              <div className="flex-1 h-px bg-gray-100" />
            </div>
          )}

          <p className="text-xs text-gray-300 mt-8 text-center">
            Markdown: # Überschrift · **fett** · *kursiv* · - Liste · &gt; Zitat
          </p>
        </div>
      </div>
    </>
  );
}
